import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Camera, X, Loader2, ImagePlus } from "lucide-react";

/**
 * Photo uploader — mostra al mobile la scelta tra fotocamera (in diretta) e galleria.
 * `value` è un array di file IDs; `onChange(newIds)` viene chiamato quando la lista cambia.
 */
export default function PhotoUploader({ value = [], onChange, testid = "photo-uploader", label }) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t("auto.foto_pre_installazione");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({}); // id -> blobUrl

  const handleFiles = async (files) => {
    setUploading(true);
    const newIds = [...value];
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const { data } = await api.post("/uploads/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push(data.id);
        // local preview from the source file (no need to re-download from server)
        setPreviews((prev) => ({ ...prev, [data.id]: URL.createObjectURL(f) }));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore upload");
      }
    }
    onChange(newIds);
    setUploading(false);
  };

  const remove = (id) => onChange(value.filter((v) => v !== id));

  const onFileInput = (ref) => (e) => {
    if (e.target.files?.length) handleFiles(Array.from(e.target.files));
    if (ref.current) ref.current.value = "";
  };

  return (
    <div data-testid={testid}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{resolvedLabel} ({value.length})</span>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-camera`}
            title="Scatta con la fotocamera"
          >
            {uploading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Camera className="mr-1 h-3 w-3" />}
            Scatta
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-gallery`}
            title="Scegli da galleria"
          >
            <ImagePlus className="mr-1 h-3 w-3" />
            Galleria
          </Button>
        </div>
      </div>
      {/* Input "Galleria": nessun capture → apre il picker file/foto */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFileInput(fileRef)}
        className="hidden"
      />
      {/* Input "Scatta": capture=environment → apre la fotocamera posteriore su mobile */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFileInput(cameraRef)}
        className="hidden"
      />
      {value.length === 0 && (
        <div className="rounded-sm border-2 border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
          {t("auto.nessuna_foto_hint")}
        </div>
      )}
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {value.map((id) => (
            <div key={id} className="group relative overflow-hidden rounded-sm border border-slate-200 bg-slate-50">
              {previews[id] ? (
                <img src={previews[id]} alt="foto" className="h-24 w-full object-cover" />
              ) : (
                <div className="grid h-24 w-full place-items-center text-xs text-slate-400 font-mono">{id.slice(-6)}</div>
              )}
              <button
                type="button"
                onClick={() => remove(id)}
                className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                data-testid={`${testid}-remove-${id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
